import { Typography } from '@euler/app/components/typography';
import { AppHoldItem, AppMenu } from '@euler/components/holdmenu/AppHoldMenu';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import {
  ItemProps,
  SectionProps,
  TableView,
} from '@euler/components/TableView';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Option } from '@euler/model';
import { memo, useMemo } from 'react';
import { StyleProp, StyleSheet, TextStyle, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';

const Section = memo(
  ({
    titleTextStyle,
    contentStyle,
    style,
    titleStyle,
    ...props
  }: SectionProps) => {
    const styles = useStyles();
    return (
      <TableView.Section
        style={[styles.section, style]}
        titleStyle={[styles.sectionTitle, titleStyle]}
        titleTextStyle={[styles.sectionTitleText, titleTextStyle]}
        contentStyle={[styles.sectionContent, contentStyle]}
        {...props}
      />
    );
  },
);
Section.displayName = 'TableSection';

const Row = memo(({ separatorStyle, style, ...props }: ItemProps) => {
  const styles = useStyles();
  return (
    <TableView.Item
      style={[styles.row, style]}
      separatorStyle={[styles.separator, separatorStyle]}
      {...props}
    />
  );
});
Row.displayName = 'TableItem';

const Select = memo(
  <T,>({
    options,
    value,
    placeholder,
    style,
    textStyle,
    placeholderStyle,
    placeholderTextColor,
    onChange,
  }: {
    options: Option<T>[];
    value?: T;
    placeholder?: string;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    placeholderTextColor?: string;
    placeholderStyle?: StyleProp<TextStyle>;
    onChange?: (value: T) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const selectedOption =
      value != null
        ? options.find(option => option.value === value)
        : undefined;

    return (
      <AppHoldItem
        presentOnPress
        placement={{
          position: 'below',
          anchor: 'left',
          align: 'left',
          matchWidth: true,
          margin: rpx(5),
        }}
        component={({ close }) => (
          <AppMenu>
            {options.map(option => (
              <AppMenu.Item
                key={String(option.value)}
                title={option.text}
                textStyle={{
                  fontSize: rpx(15),
                  fontFamily:
                    value === option.value
                      ? FontFamily.NotoSans.Medium
                      : FontFamily.NotoSans.Light,
                }}
                onPress={() => close(() => onChange?.(option.value))}
              />
            ))}
          </AppMenu>
        )}
      >
        <HStack style={style}>
          <Typography.Label
            size={rpx(16)}
            family={'medium'}
            style={[textStyle, value == null ? placeholderStyle : undefined]}
            color={value == null ? placeholderTextColor : undefined}
          >
            {value ? selectedOption?.text ?? String(value) : placeholder ?? ''}
          </Typography.Label>
          <Icon name="chevron-down" color="#999" size={rpx(24)} />
        </HStack>
      </AppHoldItem>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  const theme = useTheme();
  return useMemo(
    () =>
      StyleSheet.create({
        row: {
          paddingTop: rpx(0),
          paddingBottom: rpx(0),
          paddingLeft: 0,
          paddingRight: 0,
        },
        separator: {
          marginLeft: rpx(15),
          backgroundColor: '#f2f2f2',
        },
        section: {
          marginTop: rpx(15),
          marginLeft: rpx(24),
          marginRight: rpx(24),
        },
        sectionTitle: {
          marginLeft: rpx(5),
        },
        sectionTitleText: {
          color: theme.inspection.customIssue.labelColor,
          fontSize: rpx(14),
          lineHeight: rpx(18),
          marginBottom: rpx(5),
          fontFamily: FontFamily.NotoSans.Regular,
        },
        sectionContent: {
          backgroundColor: '#fff',
          borderRadius: rpx(8),
          borderWidth: 1,
          borderColor: '#e7e7e7',
          padding: 0,
        },
        formInput: {
          borderWidth: 0,
          backgroundColor: 'transparent',
          paddingTop: rpx(2),
          paddingBottom: rpx(2),
          paddingLeft: 0,
          paddingRight: 0,
          fontSize: rpx(16),
        },
      }),
    [rpx, theme.inspection.customIssue.labelColor],
  );
};

export const QuotationFormElements = {
  Section,
  Row,
  Select,
  useStyles,
};
